import { useMutation, useQuery } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router';

import { errorMessage } from '../api/client';
import { api } from '../api/endpoints';
import logo from '../assets/brand/maklerpass-logo.svg';
import { FullPageLoader } from '../components/Loader';
import { fmtDateTime } from '../ops/format';

const minLength = 10;

// InvitationPage: „Zugang einrichten“ aus der Einladung (OFFENE_FRAGEN B-22) – ohne Anmeldung, der Link funktioniert
// einmal. Der Benutzer legt sein Passwort selbst fest und meldet sich danach normal an.
export function InvitationPage() {
  const { token = '' } = useParams();
  const navigate = useNavigate();
  const info = useQuery({ queryKey: ['invitation', token], queryFn: ({ signal }) => api.invitation(token, signal), retry: false });
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [error, setError] = useState<string | null>(null);
  const accept = useMutation({
    mutationFn: () => api.acceptInvitation(token, password),
    onSuccess: () => navigate(`/login?eingeladen=1&email=${encodeURIComponent(info.data?.email ?? '')}`, { replace: true }),
    onError: (err) => setError(errorMessage(err)),
  });

  if (info.isPending) return <FullPageLoader label="Einladung wird geprüft …" />;

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (password.length < minLength) {
      setError(`Das Passwort braucht mindestens ${minLength} Zeichen.`);
      return;
    }
    if (password !== repeat) {
      setError('Die beiden Passwörter stimmen nicht überein.');
      return;
    }
    setError(null);
    accept.mutate();
  }

  return (
    <div className="login-page">
      <form className="login-card" onSubmit={onSubmit} noValidate aria-labelledby="invite-title">
        <div className="login-brand">
          <img className="brand-logo" src={logo} alt="Maklerpass – by Dolisa" />
        </div>
        <h1 id="invite-title">Zugang einrichten</h1>
        {info.isError ? (
          <div role="alert" className="alert alert-error">
            {errorMessage(info.error)}
          </div>
        ) : (
          <>
            <p>
              Willkommen{info.data.first_name ? `, ${info.data.first_name}` : ''}! Legen Sie Ihr Passwort fest. Danach melden Sie sich mit{' '}
              <strong>{info.data.email}</strong> an.
            </p>
            <p className="muted">Der Link gilt bis {fmtDateTime(info.data.expires_at)} und funktioniert einmal.</p>
            {error && (
              <div role="alert" className="alert alert-error">
                {error}
              </div>
            )}
            <label className="field">
              <span>Neues Passwort (mindestens {minLength} Zeichen)</span>
              <input type="password" name="new-password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} autoFocus />
            </label>
            <label className="field">
              <span>Passwort wiederholen</span>
              <input type="password" name="repeat-password" autoComplete="new-password" value={repeat} onChange={(e) => setRepeat(e.target.value)} />
            </label>
            <button type="submit" className="btn btn-primary btn-block" disabled={accept.isPending}>
              {accept.isPending ? 'Wird gespeichert …' : 'Passwort festlegen'}
            </button>
          </>
        )}
      </form>
    </div>
  );
}
